// The map controller: the choropleth (web/map.ts) with what sits on it — the legend with its month picker and labels
// switch, value labels, the zoom hint, the tooltip and numbered pins. web/main.ts's `go` calls `load` when the level,
// data point or month changes; everything else about the map's chrome lives here.
import { api, type AppConfig, type Level, type MapData, type Metric } from "./api.ts";
import { cssGradient, NO_DATA_FILL } from "./colors.ts";
import { COVERAGE } from "../shared/coverage.ts";
import { fmt, LEVEL_LABEL, metricPeriod } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import { ChoroplethMap, MIN_ZOOM, type HitPoint, type LabelPoint, type ScaleInfo, type ScaleMode, type View } from "./map.ts";

/** What the map shows: the state's level, data point and month (null = latest). */
export interface MapState { level: Level; metric: Metric; period: string | null }

export interface MapViewDeps {
  cfg: AppConfig;
  get: () => MapState;
  /** an area was clicked (its geo id; "us" on the national map) */
  onClick: (geoId: string) => void;
  /** the hovered area, if it is one of the pinned places (`level:code`), else null */
  onHoverHit: (id: string | null) => void;
  /** a month was picked in the legend (null = latest) */
  onPeriod: (period: string | null) => void;
  /** new values are on the map */
  onShown: () => void;
}

/** The elements around the map the controller fills (main.ts places them in the layout). */
export interface MapViewEls { map: HTMLElement; legend: HTMLElement; tooltip: HTMLElement; zoomHint: HTMLElement }

/** What a set of pins does: open its place, light its row in the list it came from. */
export interface PinHandlers { open: (id: string) => void; highlight: (id: string | null) => void }

/** The legend's month dropdown: the months a level and data point have, fetched once per pair. */
export class MonthPicker {
  readonly el = el("select", { class: "month", "aria-label": "Month" }) as HTMLSelectElement;
  private key = ""; // level|metric the dropdown lists
  private wanted = ""; // level|metric of the latest render call

  constructor(onPick: (period: string | null) => void) {
    this.el.addEventListener("change", () => onPick(this.el.value || null));
  }

  /** Forget the listed months (the next render fetches them again). */
  reset() { this.key = ""; }

  async render(level: Level, m: Metric, period: string | null) {
    const key = `${level}|${m.id}`;
    this.wanted = key;
    if (key === this.key) { this.el.value = period ?? ""; return; }
    try {
      const { periods } = await api.periods(level, m.id);
      if (this.wanted !== key) return; // a newer level/data point took over while this loaded
      const recent = [...periods].reverse();
      this.el.replaceChildren(el("option", { value: "" }, `Latest (${metricPeriod(m, recent[0])})`),
        ...recent.map((p) => el("option", { value: p }, metricPeriod(m, p))));
      this.el.value = period ?? "";
      this.key = key;
    } catch {
      // the previous level's months would read as this one's: list none, and the next render retries
      if (this.wanted !== key) return;
      this.el.replaceChildren();
      this.key = "";
    }
  }
}

export class MapView {
  readonly map: ChoroplethMap;
  private readonly months: MonthPicker;
  private readonly labelsToggle: HTMLElement;
  private current: MapData | undefined;
  private byCode = new Map<string, number | null>();
  private abort: AbortController | undefined;
  private seq = 0;
  private readonly centroidCache = new Map<Level, Awaited<ReturnType<typeof api.centroids>>>();
  private readonly pinSeq = { answer: 0, screen: 0 };

  constructor(private readonly els: MapViewEls, private readonly deps: MapViewDeps, view: View) {
    const { cfg, get } = deps;
    this.months = new MonthPicker(deps.onPeriod);
    this.labelsToggle = el("label", { class: "switch-toggle", title: "Show each area's value on the map when zoomed in" },
      el("input", { type: "checkbox", role: "switch", checked: true, onchange: (e: Event) => {
        this.map.setLabelsOn((e.target as HTMLInputElement).checked);
        void this.refreshLabels();
      } }), "Labels");
    const { tooltip } = els;
    this.map = new ChoroplethMap(els.map, cfg, {
      onHover: (f, pt) => {
        deps.onHoverHit(f && this.map.isHit(f.level, f.code) ? `${f.level}:${f.code}` : null);
        if (!f || !pt || !this.current) { tooltip.hidden = true; return; }
        const s = get();
        const v = this.byCode.get(s.level === "us" ? "US" : f.code);
        tooltip.hidden = false;
        tooltip.replaceChildren(el("strong", {}, f.level === "zip" ? `${f.code}` : f.name), el("br"), `${s.metric.label}: ${fmt(s.metric, v ?? null)}`);
        tooltip.style.transform = `translate(${pt.x + 14}px, ${pt.y + 14}px)`;
      },
      onClick: (f) => deps.onClick(get().level === "us" ? "us" : `${f.level}:${f.code}`),
      onZoom: () => this.updateZoomHint(),
      onScale: (sc) => this.renderLegend(sc),
    }, view);
    if (!this.map.labelsAvailable) void this.map.whenReady().then(() => { this.labelsToggle.hidden = !this.map.labelsAvailable; });
    if (!cfg.tiles) els.map.append(el("div", { class: "notice" }, "Boundary tiles are not built yet. Run ", el("code", {}, "bun run tiles"), "."));
  }

  /** Load the values for the state's level, data point and month (refetch: list the months again too). */
  async load(refetch = false) {
    if (refetch) this.months.reset();
    const mine = ++this.seq; // the abort only stops a pending fetch; this stops an older call that got past it
    const { level, metric, period } = this.deps.get();
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const data = await api.map(level, metric.id, period, this.abort.signal);
      if (mine !== this.seq) return;
      performance.mark("map:data");
      this.current = data;
      this.byCode = new Map(data.codes.map((c, i) => [c, data.values[i] ?? null]));
      await this.map.show(level, metric, data);
      if (mine !== this.seq) return;
      performance.mark("map:shown");
      void this.refreshLabels();
      void this.months.render(level, metric, this.deps.get().period);
      this.updateZoomHint();
      this.deps.onShown();
    } catch (e) {
      if ((e as Error).name === "AbortError" || mine !== this.seq) return;
      // the old values would read as the new data point's (tooltip, labels, the next pan's legend): drop them
      this.current = undefined;
      this.byCode = new Map();
      this.map.clear();
      this.map.setLabels([]);
      this.els.tooltip.hidden = true;
      this.els.legend.replaceChildren(el("div", { class: "panel-error" }, (e as Error).message));
    }
  }

  /** "Zoom in to see …" while the level's tiles aren't drawn at this zoom. */
  updateZoomHint() {
    const { level } = this.deps.get();
    const need = MIN_ZOOM[level];
    const { zoomHint } = this.els;
    zoomHint.hidden = !(this.deps.cfg.tiles && this.map.zoom() < need);
    zoomHint.textContent = `Zoom in to see ${LEVEL_LABEL[level]} areas`;
    zoomHint.onclick = () => this.map.zoomTo(need + 0.5);
  }

  /** A ranked answer's places, outlined and numbered (none on the national map). */
  pinAnswer(lvl: Level, items: Array<{ geo_id: string; label: string }>, h: PinHandlers) {
    return lvl === "us" ? this.pin("answer", [], "state", h) /* no rows: clears the pins */ : this.pin("answer", items, lvl, h);
  }

  /** The docked Screener's rows (a page at a time, `first` the page's first number), with room left for the list. */
  pinScreen(lvl: Exclude<Level, "us">, rows: Array<{ geo_id: string; label: string }> | null, first: number, h: PinHandlers) {
    const phone = innerWidth <= 900;
    return this.pin("screen", rows ?? [], lvl, h,
      { first, pad: phone ? { left: 30, right: 30, top: 230, bottom: Math.round(innerHeight * 0.42) + 20 } : { left: 440 } });
  }

  // continuous legend: gradient bar with low / median / high of the areas the colors were fitted to
  private renderLegend(sc: ScaleInfo) {
    const m = this.deps.get().metric;
    if (!this.current) return;
    const has = sc.stops.length === 11;
    const scopeBtn = (mode: ScaleMode, label: string) =>
      el("button", { class: `seg ${sc.scope === mode ? "active" : ""}`, "aria-pressed": String(sc.scope === mode),
        title: mode === "view" ? "Colors re-fit to the areas on screen as you move" : "Colors fitted to every area at this level",
        onclick: () => this.map.setScaleMode(mode) }, label);
    this.els.legend.replaceChildren(...nodes(
      el("div", { class: "legend-title" }, `${m.label} · `, this.months.el),
      has ? el("div", { class: "legend-bar", style: `background:${cssGradient(sc.colors)}` }) : el("div", { class: "muted small" }, "No data for this month"),
      has ? el("div", { class: "legend-ticks" },
        el("span", {}, fmt(m, sc.stops[0]!, true)), el("span", {}, fmt(m, sc.stops[5]!, true)), el("span", {}, fmt(m, sc.stops[10]!, true))) : null,
      // grey areas: no value; for a regional data point, say where it exists so the blank states don't read as missing data
      has ? el("div", { class: "legend-nodata small muted" }, el("span", { class: "swatch", style: `background:${NO_DATA_FILL}` }),
        m.coverage === "us" ? "No data" : `No data: ${COVERAGE[m.coverage].only}`) : null,
      el("div", { class: "legend-foot" },
        el("span", { class: "muted small" }, `${sc.n.toLocaleString()} ${sc.scope === "view" ? "areas in view" : "areas"}`),
        el("div", { class: "segmented" }, scopeBtn("view", "This view"), scopeBtn("national", "All"))),
      this.labelsToggle));
  }

  // each area's center (fetched once per level): value labels and pins
  private async centroids(lvl: Level) {
    let c = this.centroidCache.get(lvl);
    if (!c) {
      c = await api.centroids(lvl);
      this.centroidCache.set(lvl, c);
    }
    return c;
  }

  private async refreshLabels() {
    const lvl = this.deps.get().level;
    if (!this.map.labelsAvailable || !this.current || lvl === "us") { this.map.setLabels([]); return; }
    const c = await this.centroids(lvl).catch(() => null); // labels are a nicety: a failed load leaves the map without them
    const s = this.deps.get();
    if (!c || lvl !== s.level || !this.current) return;
    const byCode = this.byCode;
    const pts: LabelPoint[] = [];
    for (let i = 0; i < c.codes.length; i++) {
      const v = byCode.get(c.codes[i]!);
      if (v == null) continue;
      pts.push({ code: c.codes[i]!, lon: c.lon[i]!, lat: c.lat[i]!, text: `${lvl === "zip" ? c.codes[i] : c.names[i]}\n${fmt(s.metric, v)}` });
    }
    this.map.setLabels(pts);
  }

  /**
   * Places as numbered pins at their centers, in order (one without a center is left out; without centers, none: the
   * list they came from still works). A pin opens its place, a hover lights its row. Each kind of pins (an answer's,
   * the Screener's) drops an older call of its own kind that's still loading.
   */
  private async pin(kind: keyof MapView["pinSeq"], rows: Array<{ geo_id: string; label: string }>, lvl: Exclude<Level, "us">, h: PinHandlers,
    fit?: Parameters<ChoroplethMap["setHits"]>[3]) {
    const mine = ++this.pinSeq[kind];
    if (!rows.length) { this.map.clearHits(); return; }
    const c = await this.centroids(lvl).catch(() => null);
    await this.map.whenReady();
    if (mine !== this.pinSeq[kind] || !c) return; // a newer set (or Clear) took over
    const at = new Map(c.codes.map((code, i) => [code, i]));
    const pts: HitPoint[] = [];
    const ids: string[] = [];
    for (const r of rows) {
      const i = at.get(r.geo_id.split(":")[1]!);
      if (i == null) continue;
      pts.push({ code: c.codes[i]!, lon: c.lon[i]!, lat: c.lat[i]!, title: r.label });
      ids.push(r.geo_id);
    }
    this.map.setHits(lvl, pts, {
      onClick: (i) => h.open(ids[i]!),
      onHover: (i) => { h.highlight(i == null ? null : ids[i]!); this.map.focusHit(i == null ? null : pts[i]!.code); },
    }, fit);
  }
}
